import { css } from '@compiled/react';
import type { FC, ReactNode } from 'react';
import React from 'react';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import type { ButtonProps } from '@atlaskit/button/default/button';
import type { LinkButtonProps } from '@atlaskit/button/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';

import { ErrorPageImage } from './ErrorPageImage.tsx';
import { useErrorPageMobileLayout } from './useErrorPageMobileLayout.ts';

const ErrorContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	backgroundColor: token('elevation.surface'),
	height: '90vh',
	maxWidth: '100vw',
});

const ErrorContainerMobileStyles = css({
	height: '100vh',
});

const ErrorContainer = ({
	isMobile,
	isCenteredLayout,
	resizeContainerRef,
	children,
}: {
	isMobile: boolean;
	isCenteredLayout: boolean;
	resizeContainerRef: React.Ref<HTMLDivElement>;
	children?: React.ReactNode;
}) => {
	return (
		<div
			ref={resizeContainerRef}
			data-vc="not-found-container"
			css={[
				ErrorContainerStyles,
				isMobile && ErrorContainerMobileStyles,
				isCenteredLayout && centeredErrorContainerStyles,
			]}
		>
			{children}
		</div>
	);
};

const ErrorPageWrapperStyles = css({
	display: 'flex',
	justifyContent: 'center',
	maxWidth: '700px',
	flexDirection: 'row',
});

const ErrorPageWrapperMobileStyles = css({
	flexDirection: 'column',
});

const ErrorPageWrapper = ({
	isMobile,
	isCenteredLayout,
	children,
}: {
	isMobile: boolean;
	isCenteredLayout: boolean;
	children?: React.ReactNode;
}) => {
	return (
		<div
			css={[
				ErrorPageWrapperStyles,
				isMobile && ErrorPageWrapperMobileStyles,
				isCenteredLayout && centeredErrorPageWrapperStyles,
			]}
		>
			{children}
		</div>
	);
};

const AlignContentStyles = css({
	alignSelf: 'center',
	textAlign: 'left',
	margin: token('space.250'),
});

const AlignContentMobileStyles = css({
	textAlign: 'center',
	margin: 'initial',
});

const AlignContent = ({
	isMobile,
	isCenteredLayout,
	children,
}: {
	isMobile: boolean;
	isCenteredLayout: boolean;
	children?: React.ReactNode;
}) => {
	return (
		<div
			css={[
				AlignContentStyles,
				isMobile && AlignContentMobileStyles,
				isCenteredLayout && centeredAlignContentStyles,
			]}
		>
			{children}
		</div>
	);
};

const ErrorTitleStyles = css({
	margin: `${token('space.300')} 0 ${token('space.300')}`,
	font: token('font.heading.xxlarge'),
	fontWeight: token('font.weight.regular'),
	width: '460px',
	color: token('color.text'),
});

const ErrorTitleMobileStyles = css({
	font: token('font.heading.xlarge'),
	width: '327px',
});

const ErrorTitle = ({
	isMobile,
	isCenteredLayout,
	children,
}: {
	isMobile: boolean;
	isCenteredLayout: boolean;
	children?: React.ReactNode;
}) => {
	return (
		// eslint-disable-next-line @atlaskit/design-system/use-heading
		<h1
			css={[
				ErrorTitleStyles,
				isMobile && ErrorTitleMobileStyles,
				isCenteredLayout && centeredErrorTitleStyles,
				isCenteredLayout && isMobile && centeredErrorTitleMobileStyles,
			]}
		>
			{children}
		</h1>
	);
};

const ErrorMessageStyles = css({
	font: token('font.body'),
	fontWeight: token('font.weight.regular'),
	color: token('color.text'),
	maxWidth: '415px',
	margin: 'unset',
});

const ErrorMessageMobileStyles = css({
	font: token('font.body.large'),
	maxWidth: '327px',
	margin: '0px auto',
});

const ErrorMessage = ({
	isMobile,
	isCenteredLayout,
	children,
}: {
	isMobile: boolean;
	isCenteredLayout: boolean;
	children?: React.ReactNode;
}) => {
	return (
		<div
			css={[
				ErrorMessageStyles,
				isMobile && ErrorMessageMobileStyles,
				isCenteredLayout && centeredErrorMessageStyles,
				isMobile && isCenteredLayout && centeredErrorMessageMobileStyles,
			]}
		>
			{children}
		</div>
	);
};

const ButtonContainerStyles = css({
	display: 'flex',
	justifyContent: 'left',
});

const ButtonContainerMobileStyles = css({
	justifyContent: 'center',
});

const ButtonContainer = ({
	isMobile,
	isCenteredLayout,
	children,
}: {
	isMobile: boolean;
	isCenteredLayout: boolean;
	children?: React.ReactNode;
}) => {
	return (
		<div
			css={[
				ButtonContainerStyles,
				isMobile && ButtonContainerMobileStyles,
				isCenteredLayout && centeredButtonContainerStyles,
			]}
		>
			{children}
		</div>
	);
};
const primaryButtonStyles = xcss({
	cursor: 'pointer',
	marginTop: 'space.250',
});

const mobilePrimaryButtonStyles = xcss({
	cursor: 'pointer',
	marginTop: 'space.200',
});

const secondaryButtonStyles = xcss({
	cursor: 'pointer',
	marginTop: 'space.250',
	marginLeft: 'space.200',
});

const mobileSecondaryButtonStyles = xcss({
	cursor: 'pointer',
	marginTop: 'space.200',
	marginLeft: 'space.100',
});

const childrenStyles = xcss({
	// @ts-ignore TODO: refactor to remove need for hardcoded `-90px`
	marginTop: '-90px',
	marginInline: 'space.250',
	marginBottom: 'space.250',
	maxWidth: '700px',
});

const mobileChildrenStyles = xcss({
	// @ts-ignore TODO: refactor to remove need for hardcoded `-74px`
	marginTop: '-74px',
	marginInline: 'space.250',
	marginBottom: 'space.250',
	maxWidth: '700px',
});

type ErrorPageComponentProps = {
	errorImage?: string;
	errorImageDark?: string;
	errorTitle: ReactNode | string;
	errorMessage: ReactNode;
	primaryButtonProps?: ButtonProps | LinkButtonProps;
	secondaryButtonProps?: ButtonProps | LinkButtonProps;
	children?: React.ReactNode;
	layout?: 'default' | 'centered';
};

const centeredErrorContainerStyles = css({
	minHeight: '90vh',
	height: 'auto',
	paddingInline: token('space.300'),
	paddingBlock: token('space.500'),
	boxSizing: 'border-box',
});

const centeredErrorPageWrapperStyles = css({
	flexDirection: 'column',
	alignItems: 'center',
	width: '100%',
	maxWidth: '640px',
	gap: token('space.300'),
});

const centeredAlignContentStyles = css({
	textAlign: 'center',
	width: '100%',
	margin: '0',
});

const centeredErrorTitleStyles = css({
	margin: `0 0 ${token('space.200')}`,
	font: token('font.heading.xxlarge'),
	fontWeight: token('font.weight.bold'),
	color: token('color.text'),
	textAlign: 'center',
	width: '100%',
});

const centeredErrorTitleMobileStyles = css({
	font: token('font.heading.xxlarge'),
	fontWeight: token('font.weight.bold'),
	width: '100%',
});

const centeredErrorMessageStyles = css({
	font: token('font.body.large'),
	fontWeight: token('font.weight.regular'),
	fontFeatureSettings: "'liga' off, 'calt' off",
	color: token('color.text'),
	textAlign: 'center',
	maxWidth: '640px',
	margin: '0 auto',
});

const centeredErrorMessageMobileStyles = css({
	maxWidth: '640px',
});

const centeredButtonContainerStyles = css({
	justifyContent: 'center',
	flexWrap: 'wrap',
	gap: token('space.200'),
});

const centeredButtonStyles = xcss({
	marginTop: 'space.300',
});

const centeredChildrenStyles = xcss({
	marginTop: 'space.500',
	marginInline: 'space.250',
	marginBottom: 'space.250',
	maxWidth: '640px',
	width: '100%',
});

const centeredChildrenMobileStyles = xcss({
	marginTop: 'space.400',
	marginInline: 'space.250',
	marginBottom: 'space.250',
	maxWidth: '640px',
	width: '100%',
});

const ErrorPageChildren = ({
	isMobile,
	isCenteredLayout,
	children,
}: {
	isMobile: boolean;
	isCenteredLayout: boolean;
	children?: React.ReactNode;
}) => {
	if (!React.Children.count(children)) {
		return null;
	}

	if (isCenteredLayout) {
		if (isMobile) {
			return <Box xcss={centeredChildrenMobileStyles}>{children}</Box>;
		}

		return <Box xcss={centeredChildrenStyles}>{children}</Box>;
	}

	if (isMobile) {
		return <Box xcss={mobileChildrenStyles}>{children}</Box>;
	}

	return <Box xcss={childrenStyles}>{children}</Box>;
};

export const ErrorPageComponent: FC<ErrorPageComponentProps> = ({
	errorImage,
	errorImageDark,
	errorTitle,
	errorMessage,
	primaryButtonProps,
	secondaryButtonProps,
	children,
	layout = 'default',
}) => {
	const { resizeContainerRef, shouldUseMobileLayout } = useErrorPageMobileLayout();
	const isMobile = shouldUseMobileLayout;
	// CCCEMAU-2938: keep the centered layout added for Mohiti as an explicit opt-in so
	// legacy error pages continue using the existing shared layout until each consumer is
	// intentionally migrated.
	const isCenteredLayout = layout === 'centered';

	const renderPrimaryButton = () => {
		if (!primaryButtonProps?.children) {
			return null;
		}

		const button =
			'href' in primaryButtonProps ? (
				<LinkButton appearance="primary" {...primaryButtonProps} />
			) : (
				<Button appearance="primary" {...primaryButtonProps} />
			);

		if (isCenteredLayout && !isMobile) {
			return <Box xcss={centeredButtonStyles}>{button}</Box>;
		}

		if (isMobile) {
			return <Box xcss={mobilePrimaryButtonStyles}>{button}</Box>;
		}

		return <Box xcss={primaryButtonStyles}>{button}</Box>;
	};

	const renderSecondaryButton = () => {
		if (!secondaryButtonProps?.children) {
			return null;
		}

		const button =
			'href' in secondaryButtonProps ? (
				<LinkButton appearance="default" {...secondaryButtonProps} />
			) : (
				<Button appearance="default" {...secondaryButtonProps} />
			);

		if (isCenteredLayout && !isMobile) {
			return <Box xcss={centeredButtonStyles}>{button}</Box>;
		}

		if (isMobile) {
			return <Box xcss={mobileSecondaryButtonStyles}>{button}</Box>;
		}

		return <Box xcss={secondaryButtonStyles}>{button}</Box>;
	};

	return (
		<ErrorContainer
			isMobile={isMobile}
			isCenteredLayout={isCenteredLayout}
			resizeContainerRef={resizeContainerRef}
		>
			<ErrorPageWrapper isMobile={isMobile} isCenteredLayout={isCenteredLayout}>
				<AlignContent isMobile={isMobile} isCenteredLayout={isCenteredLayout}>
					{errorImage && (
						<ErrorPageImage
							src={errorImage}
							srcDark={errorImageDark}
							isMobile={isMobile}
							isCenteredLayout={isCenteredLayout}
						/>
					)}
				</AlignContent>
				<AlignContent isMobile={isMobile} isCenteredLayout={isCenteredLayout}>
					<ErrorTitle isMobile={isMobile} isCenteredLayout={isCenteredLayout}>
						{errorTitle}
					</ErrorTitle>
					<ErrorMessage isMobile={isMobile} isCenteredLayout={isCenteredLayout}>
						{errorMessage}
					</ErrorMessage>
					{(primaryButtonProps?.children || secondaryButtonProps?.children) && (
						<ButtonContainer isMobile={isMobile} isCenteredLayout={isCenteredLayout}>
							{renderPrimaryButton()}
							{renderSecondaryButton()}
						</ButtonContainer>
					)}
				</AlignContent>
			</ErrorPageWrapper>
			<ErrorPageChildren isMobile={isMobile} isCenteredLayout={isCenteredLayout}>
				{children}
			</ErrorPageChildren>
		</ErrorContainer>
	);
};
